import { IPromise } from 'angular'
import { StoreNames, StoreValue } from 'idb'
import partition from 'lodash/partition'

import { $q } from '../../ng-compat/imports'
import { EventEmitter } from '../../util/event-emitter'
import { Store, store } from './database'
import { WebRegisterDb } from './database-types'

interface Config<StoreName extends StoreNames<WebRegisterDb>> {
  entityStoreName: string
  entityName?: string
  databaseStoreName?: StoreName
  filterByOutlet?: boolean
  syncDeleted?: boolean
}

export interface SyncData<StoreName extends StoreNames<WebRegisterDb>> {
  updated: Array<StoreValue<WebRegisterDb, StoreName>>
  removed: Array<StoreValue<WebRegisterDb, StoreName>>
  versionData?: VersionData
}

export interface VersionData {
  lastSyncedVersion?: number
  payloadVersion?: object
}

type Events<StoreName extends StoreNames<WebRegisterDb>> = {
  sync: [SyncData<StoreName>]
}

export class EntityStore<
  StoreName extends StoreNames<WebRegisterDb>
> extends EventEmitter<Events<StoreName>> {
  /**
   * The IndexedDB store where the entity data is stored.
   */
  protected dbStore: Store<StoreName>

  /**
   * Name of this entity store.
   */
  public entityStoreName: string

  /**
   * Name of the entity stored in this store. Must match the api end point.
   */
  public entityName: string

  /**
   * Whether to filter this entity by the current active outlet.
   */
  public filterByOutlet: boolean

  /**
   * Whether to include deleted entities in the data sync.
   */
  public syncDeleted: boolean

  /**
   * Abstract implementation of an entity store - a class responsible for syncing and managing the data for a
   * specific entity e.g. products or outlets.
   *
   * @param  config
   *
   *         @param config.entityStoreName
   *                The IndexedDB store where the entity data is stored.
   *
   *         @param [config.entityName]
   *                Name of this entity store. Defaults to the same as the `entityStoreName`.
   *
   *         @param [config.databaseStoreName]
   *                Name of the entity stored in this store. Must match the api end point. Defaults to the same as
   *                the `entityStoreName`.
   *
   *         @param [config.filterByOutlet]
   *                Whether to filter this entity by the current active outlet. Defaults to false.
   *
   *         @param [config.syncDeleted]
   *                Whether to include deleted entities in the data sync. Defaults to false.
   */
  constructor(config: Config<StoreName>) {
    super()

    this.dbStore = store(
      config.databaseStoreName || (config.entityStoreName as StoreName)
    )

    this.entityStoreName = config.entityStoreName

    this.entityName = config.entityName || config.entityStoreName

    this.filterByOutlet = Boolean(config.filterByOutlet)

    this.syncDeleted = Boolean(config.syncDeleted)
  }

  /**
   * Sync the given entity data with the IndexedDB store.
   *
   * @param entities
   *        Array of entity objects as returned by the relevant API end point
   *
   * @param [versionData]
   *        Optional information on the sync versions contained within the payload and of the relevant entity.
   *        @note This should only be set when performing a versioned sync of this entity.
   *
   *        @param [versionData.lastSyncedVersion]
   *               The last synced version of the relevant entity prior to this payload
   *
   *        @param [versionData.payloadVersion]
   *               Object containing the `min` and `max` version represented in the given payload
   *
   * @return A promise resolved once the given entity data has been synced with the IndexedDB store
   */
  public syncData(
    entities: Array<StoreValue<WebRegisterDb, StoreName>>,
    versionData?: VersionData
  ): IPromise<SyncData<StoreName>> {
    if (!entities.length) {
      // Nothing to sync, return a fulfilled promise
      return $q.resolve({ updated: [], removed: [] })
    }

    const [updates, removes] = partition(entities, entity =>
      this.shouldStoreEntity(entity)
    )

    return $q
      .all([this.updateEntities(updates), this.removeEntities(removes)])
      .then(([updated, removed]) => {
        const syncData: SyncData<StoreName> = { updated, removed }
        if (versionData) {
          syncData.versionData = versionData
        }
        return syncData
      })
      .then(syncData => this.emitSyncEvent(syncData))
  }

  /**
   * Sync the given entity data with the IndexedDB store. This returns native Promise instead of Angular's IPromise.
   */
  public syncDataInNativePromise(
    entities: Array<StoreValue<WebRegisterDb, StoreName>>,
    versionData?: VersionData
  ): Promise<SyncData<StoreName>> {
    if (!entities.length) {
      // Nothing to sync, return a fulfilled promise
      return Promise.resolve({ updated: [], removed: [] })
    }

    const [updates, removes] = partition(entities, entity =>
      this.shouldStoreEntity(entity)
    )

    return Promise.all([
      this.updateEntities(updates),
      this.removeEntities(removes, true),
    ])
      .then(([updated, removed]) => {
        const syncData: SyncData<StoreName> = { updated, removed }
        if (versionData) {
          syncData.versionData = versionData
        }
        return syncData
      })
      .then(syncData => this.emitSyncEvent(syncData))
  }

  /**
   * Refresh (replace) the entity data in the IndexedDB store with the entity data on the server.
   *
   * @return A promise resolved once the entity data has been refreshed from the server
   */
  public refresh() {
    return this.fetchRefreshedEntityDataFromServer().then(
      this.processRefreshedData.bind(this)
    )
  }

  /**
   * Get entity information by entity ID.
   *
   * @param id
   *        The ID of the entity to retrieve from the database
   * @param [returnNativePromise]
   *        Whether to return a native promise or an Angular promise. Defaults to false.
   *
   * @return A promise resolved with the requested entity's details
   */
  public get(id: string, returnNativePromise = false) {
    return returnNativePromise
      ? this.dbStore.get(id)
      : $q.resolve(this.dbStore.get(id))
  }

  /**
   * Count the number of entities of this type in the IndexedDB store.
   *
   * @return A promise resolved with the count of all entities in the IndexedDB store
   */
  public count(): Promise<number> {
    return this.dbStore.count()
  }

  /**
   * Store the given value optionally against the given key.
   *
   * @param  [key]
   *         The key to store the value against
   *
   * @param  value
   *         The value to store in the store
   *
   * @return A promise resolved once the given value has been stored
   */
  public put(key: any, value?: any) {
    return $q.resolve(this.dbStore.put.apply(this.dbStore, arguments))
  }

  /**
   * Get all the entities of this type in the IndexedDB store.
   *
   * @return A promise resolved with the array of all entities in the IndexedDB store
   */
  public all() {
    return $q.resolve(this.dbStore.all())
  }

  /**
   * Clears all entity data from the IndexedDB store.
   *
   * @return A promise resolved once the IndexedDB store has been cleared
   */
  public clear() {
    return $q.resolve(this.dbStore.clear())
  }

  /**
   * Get the information if a entity should be persisted or not
   *
   * @return {boolean} true
   */
  public shouldBeSynchronized(): boolean {
    return true
  }

  /**
   * Executes any processing that needs to be done after initializing the entity
   *
   * @return {IPromise<any>}
   */
  public postInitProcess(): IPromise<any> {
    return $q.when()
  }

  /**
   * Get the url of the entity
   *
   * @returns {string}
   */
  public getUrl(entitySyncConfig: $TSFixMe): string {
    const { entityVersion } = entitySyncConfig.data
    return `/api/2.0/${entityVersion.entityName}`
  }

  /**
   * Process the refreshed data returned by the server. By default clears the IndexedDB store and then invokes
   * `syncData` with the refreshed data.
   *
   * @param  refreshedData
   *         The refreshed data returned by the server
   *
   * @return A promise resolved with the refreshed data once it has been processed
   */
  protected processRefreshedData(
    refreshedData: Array<StoreValue<WebRegisterDb, StoreName>>
  ) {
    return this.clear()
      .then(() => this.syncData(refreshedData))
      .then(() => refreshedData)
  }

  /**
   * Fetch the latest entity data from the server for refreshing.
   *
   * @return A promise resolved with the latest entity data from the server
   */
  protected fetchRefreshedEntityDataFromServer(): IPromise<
    Array<StoreValue<WebRegisterDb, StoreName>>
  > {
    return $q.reject('refresh() is not implemented by this entity store')
  }

  /**
   * Determine whether a given entity should be saved to the IndexedDB store or removed (if it exists in the
   * store). Default implementation checks for a falsey `deleted_at` property to confirm it should be saved.
   *
   * @param  entityData
   *         The data for the given entity as returned by the relevant API end point
   *
   * @return true if the entity should be stored, false if it should not be stored (and potentially
   *         removed)
   */
  protected shouldStoreEntity(
    entityData: StoreValue<WebRegisterDb, StoreName>
  ) {
    return this.syncDeleted || !entityData.deleted_at
  }

  /**
   * Add or update the given entities in the IndexedDB store.
   *
   * @param  entities
   *         The array of entities to add/update
   *
   * @return A promise resolved with the array of entities that were successfully updated
   */
  protected updateEntities(
    entities: Array<StoreValue<WebRegisterDb, StoreName>>
  ) {
    return this.dbStore.batch(entities).then(() => entities)
  }

  /**
   * Remove the given entities from the IndexedDB store.
   *
   * @param  entities
   *         The array of entities to remove
   *
   * @return A promise resolved with the array of entities that were successfully removed
   */
  protected removeEntities(
    entities: Array<StoreValue<WebRegisterDb, StoreName>>,
    returnNativePromise = false
  ) {
    if (returnNativePromise) {
      return Promise.all(entities.map(this.removeEntity, this)).then(
        () => entities
      )
    }
    return $q.all(entities.map(this.removeEntity, this)).then(() => entities)
  }

  /**
   * Remove the given entity from the IndexedDB store.
   *
   * @param  entity
   *         The entity to remove from the store
   *
   * @return A promise resolved once the entity has been successfully removed from the store
   */
  protected removeEntity(entity: StoreValue<WebRegisterDb, StoreName>) {
    return this.dbStore.del(entity.id)
  }

  /**
   * Emit the `sync` event with the given sync data (arrays of `updated` and `removed` entities).
   *
   * @param  syncData
   *
   * @return The given sync data for promise chaining purposes
   */
  protected emitSyncEvent(syncData: SyncData<StoreName>) {
    this.emit('sync', syncData)
    return syncData
  }
}
